import { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { X, Send, Mic, MicOff, Volume2, VolumeX, Trash2, Wifi } from 'lucide-react'
import { useMaiaStore, type UIAction } from '../../core/stores/maiaStore'
import { useSpeechRecognition } from '../../hooks/useSpeechRecognition'
import { speakText, stopSpeaking } from '../../lib/voiceUtils'

export default function MaiaChatPanel() {
  const {
    isOpen, close, messages, isLoading, sendMessage, clearMessages,
    voiceMode, setVoiceMode, isRecording, setRecording,
    isSpeaking, setSpeaking, wakeWordEnabled, setWakeWordEnabled,
    setCurrentPage, micPermission, requestMicPermission,
  } = useMaiaStore()

  const [input, setInput] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const navigate = useNavigate()
  const location = useLocation()

  // Aggiorna current page
  useEffect(() => {
    setCurrentPage(location.pathname)
  }, [location.pathname, setCurrentPage])

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, isLoading])

  // Auto-grow textarea
  useEffect(() => {
    const el = inputRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = Math.min(el.scrollHeight, 120) + 'px'
  }, [input])

  // Ref per startRecognition — spezza la dipendenza circolare handleSend ↔ useSpeechRecognition
  const startRecognitionRef = useRef<() => void>(() => {})
  const stopRecognitionRef = useRef<() => void>(() => {})
  // Traccia se l'utente ha esplicitamente mutato il mic (default: false = mic attivo)
  const userMutedMicRef = useRef(false)

  // Process UI actions
  const processUIActions = useCallback((actions: UIAction[]) => {
    for (const action of actions) {
      if (action.type === 'navigate' && action.path) {
        navigate(action.path)
      }
    }
  }, [navigate])

  // Send handler — dopo invio riavvia sempre il mic (a meno che utente non l'abbia mutato)
  const handleSend = useCallback(async () => {
    const text = input.trim()
    if (!text || isLoading) return
    setInput('')
    const actions = await sendMessage(text)
    processUIActions(actions)

    const shouldRestartMic = !userMutedMicRef.current

    if (voiceMode) {
      // Voice mode: TTS + riavvia mic dopo
      const msgs = useMaiaStore.getState().messages
      const lastMsg = msgs[msgs.length - 1]
      if (lastMsg?.role === 'assistant') {
        setSpeaking(true)
        speakText(lastMsg.content.slice(0, 300), () => {
          setSpeaking(false)
          if (shouldRestartMic) {
            setTimeout(() => startRecognitionRef.current(), 300)
          }
        })
      } else if (shouldRestartMic) {
        setTimeout(() => startRecognitionRef.current(), 300)
      }
    } else if (shouldRestartMic) {
      // No voice mode ma mic era attivo: riavvia mic senza TTS
      setTimeout(() => startRecognitionRef.current(), 300)
    }
  }, [input, isLoading, sendMessage, processUIActions, voiceMode, setSpeaking])

  // Keyboard
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  // Speech recognition
  const { start: startRecognition, stop: stopRecognition, isListening, syncTranscript } = useSpeechRecognition({
    onTranscript: (text) => setInput(text),
    onSendCommand: () => handleSend(),
  })
  startRecognitionRef.current = startRecognition
  stopRecognitionRef.current = stopRecognition

  useEffect(() => {
    setRecording(isListening)
  }, [isListening, setRecording])

  // Auto-start mic quando il pannello si apre
  // NOTA: autoStartRecording NON è nelle deps per evitare double-fire.
  // openWithVoice() setta isOpen + autoStartRecording atomicamente, quindi
  // il useEffect li vede entrambi nel primo (e unico) giro.
  const micStartPendingRef = useRef(false)
  useEffect(() => {
    if (isOpen) {
      // Previeni doppia partenza
      if (micStartPendingRef.current) return
      micStartPendingRef.current = true

      // Controlla se aperto da wake word (autoStartRecording = true)
      const fromWake = useMaiaStore.getState().autoStartRecording
      if (fromWake) {
        useMaiaStore.getState().setAutoStartRecording(false)
        userMutedMicRef.current = false
      }

      // Avvia mic automaticamente se utente non ha mutato esplicitamente
      if (!userMutedMicRef.current) {
        // Delay più lungo da wake word: Chrome deve rilasciare completamente il mic
        const delay = fromWake ? 800 : 300
        const tryStartMic = async () => {
          let perm = useMaiaStore.getState().micPermission
          // Se permesso non ancora concesso, richiedilo (mostra dialog browser)
          if (perm !== 'granted' && perm !== 'insecure' && perm !== 'denied') {
            const ok = await useMaiaStore.getState().requestMicPermission()
            if (!ok) { micStartPendingRef.current = false; return }
          }
          perm = useMaiaStore.getState().micPermission
          if (perm === 'granted') {
            setTimeout(() => {
              startRecognitionRef.current()
              micStartPendingRef.current = false
            }, delay)
          } else {
            micStartPendingRef.current = false
          }
        }
        tryStartMic()
      } else {
        micStartPendingRef.current = false
      }
    } else {
      // Pannello chiuso: ferma recognition, svuota input, resetta flag
      micStartPendingRef.current = false
      stopRecognitionRef.current()
      userMutedMicRef.current = false
      setInput('')
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const toggleMic = async () => {
    if (isListening) {
      stopRecognition()
      userMutedMicRef.current = true  // Utente ha esplicitamente mutato
    } else {
      userMutedMicRef.current = false  // Utente ha esplicitamente riattivato
      if (micPermission !== 'granted') {
        const ok = await requestMicPermission()
        if (!ok) return
      }
      startRecognition()
    }
  }

  const toggleVoice = () => {
    if (isSpeaking) {
      stopSpeaking()
      setSpeaking(false)
    }
    setVoiceMode(!voiceMode)
  }

  if (!isOpen) return null

  return (
    <div
      className={`
        fixed bottom-4 z-50 flex flex-col
        w-[480px] max-h-[600px] rounded-xl shadow-2xl overflow-hidden
        border
        left-1/2 -translate-x-1/2 ml-32
        max-[768px]:w-[calc(100vw-2rem)] max-[768px]:ml-0
      `}
      style={{
        backgroundColor: 'var(--color-bg-card, #1a1a2e)',
        borderColor: 'var(--color-border, #2a2a4a)',
        color: 'var(--color-text-primary, #e0e0e0)',
      }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3 border-b"
        style={{ borderColor: 'var(--color-border, #2a2a4a)', backgroundColor: 'var(--color-bg-surface, #12122a)' }}
      >
        <div className="flex items-center gap-2">
          <span className="text-lg font-bold" style={{ color: 'var(--color-primary, #6366f1)' }}>ELETTRA</span>
          <span
            className="text-[10px] px-1.5 py-0.5 rounded-full font-medium"
            style={{
              backgroundColor: isLoading ? 'var(--color-warning, #f59e0b)' : 'var(--color-success, #22c55e)',
              color: '#000',
            }}
          >
            {isLoading ? 'THINKING...' : 'ONLINE'}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={async () => {
              if (!wakeWordEnabled && micPermission !== 'granted') {
                const ok = await requestMicPermission()
                if (!ok) return
              }
              setWakeWordEnabled(!wakeWordEnabled)
            }}
            className="p-1.5 rounded-lg transition-colors hover:opacity-80"
            style={{
              backgroundColor: wakeWordEnabled ? 'var(--color-primary, #6366f1)' : 'transparent',
              color: wakeWordEnabled ? '#fff' : 'var(--color-text-secondary, #888)',
            }}
            title={wakeWordEnabled ? 'Wake word attivo' : 'Wake word disattivo'}
          >
            <Wifi className="w-4 h-4" />
          </button>
          <button
            onClick={toggleVoice}
            className="p-1.5 rounded-lg transition-colors hover:opacity-80"
            style={{
              backgroundColor: voiceMode ? 'var(--color-primary, #6366f1)' : 'transparent',
              color: voiceMode ? '#fff' : 'var(--color-text-secondary, #888)',
            }}
            title={voiceMode ? 'Voce attiva' : 'Voce disattiva'}
          >
            {voiceMode ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
          </button>
          <button
            onClick={clearMessages}
            className="p-1.5 rounded-lg transition-colors hover:opacity-80"
            style={{ color: 'var(--color-text-secondary, #888)' }}
            title="Cancella chat"
          >
            <Trash2 className="w-4 h-4" />
          </button>
          <button
            onClick={close}
            className="p-1.5 rounded-lg transition-colors hover:opacity-80"
            style={{ color: 'var(--color-text-secondary, #888)' }}
            title="Chiudi (Ctrl+M)"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Mic permission banner */}
      {micPermission === 'insecure' && (
        <div className="px-4 py-2 text-xs text-center" style={{ backgroundColor: 'rgba(251, 191, 36, 0.15)', color: '#fbbf24' }}>
          Voce non disponibile su HTTP. Serve HTTPS per il microfono. La chat testuale funziona normalmente.
        </div>
      )}
      {micPermission === 'denied' && (
        <div className="px-4 py-2 text-xs text-center" style={{ backgroundColor: 'rgba(239, 68, 68, 0.15)', color: '#f87171' }}>
          Microfono bloccato. Nelle impostazioni del browser, consenti il microfono per questo sito.
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" style={{ minHeight: '120px' }}>
        {messages.length === 0 && (
          <div className="text-center py-8" style={{ color: 'var(--color-text-tertiary, #666)' }}>
            <p className="text-sm">Ciao! Sono ELETTRA, il tuo assistente.</p>
            <p className="text-xs mt-1">Chiedimi di cercare offerte, aprire fascicoli, creare commesse...</p>
          </div>
        )}
        {messages.map((msg) => (
          <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`max-w-[85%] px-3 py-2 rounded-xl text-sm whitespace-pre-wrap ${
                msg.role === 'user' ? 'rounded-br-sm' : 'rounded-bl-sm'
              }`}
              style={{
                backgroundColor: msg.role === 'user'
                  ? 'var(--color-primary, #6366f1)'
                  : 'var(--color-bg-surface, #1e1e3a)',
                color: msg.role === 'user' ? '#fff' : 'var(--color-text-primary, #e0e0e0)',
              }}
            >
              {msg.content}
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="flex justify-start">
            <div
              className="px-3 py-2 rounded-xl rounded-bl-sm text-sm"
              style={{ backgroundColor: 'var(--color-bg-surface, #1e1e3a)' }}
            >
              <span className="animate-pulse" style={{ color: 'var(--color-primary, #6366f1)' }}>
                ELETTRA sta pensando...
              </span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="px-4 py-3 border-t" style={{ borderColor: 'var(--color-border, #2a2a4a)' }}>
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => { setInput(e.target.value); syncTranscript(e.target.value) }}
            onKeyDown={handleKeyDown}
            placeholder="Scrivi a ELETTRA..."
            rows={1}
            className="flex-1 resize-none rounded-lg px-3 py-2 text-sm outline-none"
            style={{
              backgroundColor: 'var(--color-bg-input, #0f0f24)',
              color: 'var(--color-text-primary, #e0e0e0)',
              border: '1px solid var(--color-border, #2a2a4a)',
              overflow: 'auto',
            }}
          />
          <button
            onClick={toggleMic}
            className="p-2 rounded-lg transition-colors"
            style={{
              backgroundColor: isRecording ? '#ef4444' : 'transparent',
              color: isRecording ? '#fff' : 'var(--color-text-secondary, #888)',
            }}
            title={isRecording ? 'Stop dettatura' : 'Inizia dettatura'}
          >
            {isRecording ? <Mic className="w-5 h-5 animate-pulse" /> : <MicOff className="w-5 h-5" />}
          </button>
          <button
            onClick={handleSend}
            disabled={!input.trim() || isLoading}
            className="p-2 rounded-lg transition-colors disabled:opacity-30"
            style={{
              backgroundColor: 'var(--color-primary, #6366f1)',
              color: '#fff',
            }}
            title="Invia (Enter)"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  )
}
